import { saveAs } from 'file-saver';
import JSZip from 'jszip';

export type ImageFormat = 'jpeg' | 'png' | 'webp' | 'avif' | 'gif' | 'bmp' | 'ico' | 'svg';

export type ScaleOption = 0.5 | 0.75 | 1 | 1.5 | 2;

// ---------------------------------------------------------------------------
// Browser format-support detection
// ---------------------------------------------------------------------------

const formatSupportCache = new Map<string, boolean>();

const ALL_OUTPUT_CANDIDATES: ImageFormat[] = ['jpeg', 'png', 'webp', 'avif', 'gif', 'bmp', 'ico'];

export const isFormatSupported = async (format: string): Promise<boolean> => {
  const mimeType = `image/${format}`;
  if (formatSupportCache.has(mimeType)) return formatSupportCache.get(mimeType)!;

  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    canvas.toBlob(
      (blob) => {
        const supported = blob !== null && blob.type === mimeType;
        formatSupportCache.set(mimeType, supported);
        resolve(supported);
      },
      mimeType,
      0.5
    );
  });
};

/** Warm the cache on startup so format lists are instant. */
export const initFormatSupport = async (): Promise<void> => {
  await Promise.all(ALL_OUTPUT_CANDIDATES.map(f => isFormatSupported(f)));
};

// ---------------------------------------------------------------------------
// Available output formats
// ---------------------------------------------------------------------------

/**
 * Returns only formats the current browser can encode.
 * If all uploaded files share one format, that format is excluded.
 */
export const getAvailableOutputFormats = (files: File[]): ImageFormat[] => {
  const sourceFormats = new Set<string>();
  files.forEach(file => {
    sourceFormats.add(file.type.toLowerCase().replace('image/', ''));
  });

  const cacheReady = ALL_OUTPUT_CANDIDATES.some(f => formatSupportCache.has(`image/${f}`));
  const allFormats = cacheReady
    ? ALL_OUTPUT_CANDIDATES.filter(f => formatSupportCache.get(`image/${f}`) === true)
    : (['jpeg', 'png', 'webp', 'gif', 'bmp', 'ico'] as ImageFormat[]);

  if (sourceFormats.size === 1) {
    const src = Array.from(sourceFormats)[0];
    return allFormats.filter(f => f !== src);
  }

  return allFormats;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UploadedImage {
  id: string;
  file: File;
  size: number;
  previewUrl: string;
}

export interface ConvertedImage {
  id: string;
  originalFile: File;
  originalSize: number;
  convertedBlob: Blob;
  convertedSize: number;
  format: ImageFormat;
  compressionLevel: number;
  scale: ScaleOption;
  previewUrl: string;
}

// ---------------------------------------------------------------------------
// Quality tuning
// ---------------------------------------------------------------------------

/**
 * Maps the user's 0-100 slider to an effective quality value per format.
 * Goal: best visual quality at the smallest file size.
 *
 *  JPEG  — pass-through; browsers handle this well linearly
 *  WebP  — quality above 80 gives diminishing returns, cap at 85
 *  AVIF  — extremely efficient; map 0-100 → 10-50 range
 *  PNG   — lossless, quality param is ignored by the encoder
 *  GIF   — palette-based, quality param is largely irrelevant
 *  BMP/ICO — uncompressed, quality param unused
 */
export const getEffectiveQuality = (
  format: ImageFormat,
  userLevel: number
): number => {
  switch (format) {
    case 'avif':
      return Math.round(10 + (userLevel / 100) * 40);   // 10–50
    case 'webp':
      return Math.min(userLevel, 85);                     // cap at 85
    default:
      return userLevel;
  }
};

// ---------------------------------------------------------------------------
// Canvas conversion (main thread fallback)
// ---------------------------------------------------------------------------

const convertOnCanvas = (
  file: File,
  format: ImageFormat,
  quality: number,
  scale: ScaleOption
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      if (!e.target?.result) return reject(new Error('Failed to read file'));

      const img = new Image();
      img.onload = () => {
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Failed to get canvas context'));

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        if (['jpeg', 'bmp', 'ico'].includes(format)) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
        }

        ctx.drawImage(img, 0, 0, w, h);

        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))),
          `image/${format}`,
          quality / 100
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

// ---------------------------------------------------------------------------
// Web Worker conversion (off main thread)
// ---------------------------------------------------------------------------

const supportsOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';
const WORKER_TIMEOUT_MS = 30_000;

const convertInWorker = (
  file: File,
  format: ImageFormat,
  quality: number,
  scale: ScaleOption
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    let settled = false;
    const worker = new Worker(
      new URL('../workers/converter.worker.ts', import.meta.url),
      { type: 'module' }
    );

    const timeoutId = setTimeout(() => {
      if (!settled) { settled = true; worker.terminate(); reject(new Error('Worker timed out')); }
    }, WORKER_TIMEOUT_MS);

    worker.onmessage = (e: MessageEvent) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      worker.terminate();
      if (e.data.error) reject(new Error(e.data.error));
      else resolve(new Blob([e.data.buffer], { type: e.data.type }));
    };

    worker.onerror = (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      worker.terminate();
      reject(new Error(err.message || 'Worker error'));
    };

    worker.postMessage({ imageData: file, format, compressionLevel: quality, scaleOption: scale, fileName: file.name });
  });

// ---------------------------------------------------------------------------
// SVG → raster  (always main thread — worker can't load SVG reliably)
// ---------------------------------------------------------------------------

const convertSvgToRaster = (
  file: File,
  format: string,
  quality: number,
  scale: number
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (!event.target?.result) return reject(new Error('Failed to read SVG'));

      const svgString = event.target.result as string;
      const viewBoxMatch = svgString.match(/viewBox=["']([^"']*)["']/i);
      const widthMatch = svgString.match(/width=["']([^"']*)["']/i);
      const heightMatch = svgString.match(/height=["']([^"']*)["']/i);

      let w = 800, h = 600;

      if (viewBoxMatch?.[1]) {
        const v = viewBoxMatch[1].split(/[\s,]+/);
        if (v.length >= 4) { w = parseInt(v[2], 10); h = parseInt(v[3], 10); }
      }
      if (!w || !h || w <= 0 || h <= 0) {
        if (widthMatch?.[1]) w = parseFloat(widthMatch[1].replace(/[^0-9.]/g, ''));
        if (heightMatch?.[1]) h = parseFloat(heightMatch[1].replace(/[^0-9.]/g, ''));
      }
      if (!w || !h || w <= 0 || h <= 0) { w = 800; h = 600; }

      w = Math.round(w * scale);
      h = Math.round(h * scale);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Failed to get canvas context'));

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))),
          `image/${format}`,
          quality / 100
        );
      };
      img.onerror = () => reject(new Error('Failed to load SVG'));

      let data = svgString;
      if (!data.includes('xmlns="http://www.w3.org/2000/svg"'))
        data = data.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(data)}`;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });

// ---------------------------------------------------------------------------
// Main conversion entry point
// ---------------------------------------------------------------------------

const progress = (fileName: string, pct: number) =>
  window.dispatchEvent(new CustomEvent('conversionProgress', { detail: { progress: pct, fileName } }));

export const convertImage = async (
  file: File,
  format: ImageFormat,
  userQuality: number,
  scale: ScaleOption
): Promise<ConvertedImage> => {
  const quality = getEffectiveQuality(format, userQuality);

  const supported = await isFormatSupported(format);
  if (!supported) {
    throw new Error(`Your browser does not support encoding to ${format.toUpperCase()}. Try a different browser or format.`);
  }

  progress(file.name, 10);

  let blob: Blob;

  if (file.type === 'image/svg+xml') {
    blob = await convertSvgToRaster(file, format, quality, scale);
  } else if (supportsOffscreenCanvas) {
    try {
      blob = await convertInWorker(file, format, quality, scale);
    } catch {
      blob = await convertOnCanvas(file, format, quality, scale);
    }
  } else {
    blob = await convertOnCanvas(file, format, quality, scale);
  }

  progress(file.name, 90);

  const previewUrl = URL.createObjectURL(blob);
  progress(file.name, 100);

  return {
    id: `${file.name}-${Date.now()}`,
    originalFile: file,
    originalSize: file.size,
    convertedBlob: blob,
    convertedSize: blob.size,
    format,
    compressionLevel: quality,
    scale,
    previewUrl,
  };
};

// ---------------------------------------------------------------------------
// Download helpers
// ---------------------------------------------------------------------------

export const downloadImage = async (image: ConvertedImage): Promise<void> => {
  if (!image?.convertedBlob) throw new Error(`No converted data for "${image.originalFile.name}"`);

  const fileName = `${image.originalFile.name.split('.')[0]}.${image.format}`;
  const url = URL.createObjectURL(image.convertedBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  await new Promise(r => setTimeout(r, 200));
};

export const downloadAllImages = async (images: ConvertedImage[]): Promise<void> => {
  if (images.length === 0) throw new Error('No images to download');

  const zip = new JSZip();
  images.forEach(img => {
    zip.file(`${img.originalFile.name.split('.')[0]}.${img.format}`, img.convertedBlob);
  });
  const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 9 } });
  saveAs(zipBlob, 'converted-images.zip');
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export const estimateOutputSize = (
  originalSize: number,
  format: ImageFormat,
  compressionLevel: number
): number => {
  let ratio: number;
  switch (format) {
    case 'avif': ratio = 0.25 * (compressionLevel / 100); break;
    case 'webp': ratio = 0.45 * (compressionLevel / 100); break;
    case 'jpeg': ratio = 0.65 * (compressionLevel / 100); break;
    case 'png':  ratio = 0.85; break;
    case 'gif':  ratio = 0.80; break;
    case 'bmp':  ratio = 1.20; break;
    case 'ico':  ratio = 0.80; break;
    default:     ratio = 0.70;
  }
  if (compressionLevel < 30 && ['jpeg', 'webp', 'avif'].includes(format)) ratio *= 0.7;
  return originalSize * ratio;
};

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = Math.max(0, decimals);
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const calculateCompressionPercentage = (original: number, compressed: number): string =>
  (((original - compressed) / original) * 100).toFixed(1);

export const handleFiles = async (files: FileList | File[]): Promise<File[]> => {
  const validTypes = [
    'image/jpeg', 'image/png', 'image/webp', 'image/gif',
    'image/bmp', 'image/svg+xml', 'image/avif',
  ];
  const imageFiles = Array.from(files).filter(f => validTypes.includes(f.type.toLowerCase()));

  if (imageFiles.length === 0) throw new Error('Please select valid image files (JPEG, PNG, WebP, etc.)');

  const maxSize = 10 * 1024 * 1024;
  const oversized = imageFiles.filter(f => f.size > maxSize);
  if (oversized.length > 0)
    throw new Error(`Files must be under 10 MB. Too large: ${oversized.map(f => f.name).join(', ')}`);

  return imageFiles;
};
